import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import {
  fetchRestaurantReservationList,
  analyticsReservationListSelector,
  fetchTotalReservationsStats,
  totalReservationsStatsSelector,
} from '../../analytics/AnalyticsSlice';
import TablePaginationActions from '../../../shared/table-pagination/TablePaginationActions';
import moment from 'moment';
import CardWrapper from '../CardWrapper';
import {
  generateReservationExportData,
  generatePdfTable,
  generateTotalReservationExportData,
  generateChartPdfExportData,
  getAllHotelDetails,
} from '../../../utils/analyticUtils';
import './reservations.scss';
import SummaryCardWrapper from '../SummaryCardWrapper';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
//temporarily disable
// import zc from "@dvsl/zoomcharts";
import CustomLineChart from '../../../shared/charts/custom-line-chart/CustomLineChart';
import Restaurant from '../../statistics/restaurant/Restaurant';
import WidgetWrapper from '../WidgetWrapper';
import { statisticsDataKeys } from '../Analytics';
import { notificationManager } from '../../../utils/NotificationUtils';
import { ENVIRONMENT } from '../../../env/Configurations';
import { filteredHotelsSelector, selectedHotelSelector } from '../../property/propertySlice';
import { getSelectedHotelName } from '../../../utils/commonUtils';

const Reservations = (props) => {
  const {
    startDate,
    endDate,
    hotelId,
    onsetPage,
    page,
    onsetRowsPerPage,
    rowsPerPage,
    restaurantId,
    showLoader = () => {},
    showAllForReport = false,
    setShowAllForReport = () => {},
  } = props;
  const dispatch = useDispatch();
  const reservationTableData = useSelector(analyticsReservationListSelector);
  const totalReservationData = useSelector(totalReservationsStatsSelector);
  const selectedHotel = useSelector(selectedHotelSelector);
  const selectedHotelList = useSelector(filteredHotelsSelector);
  //temporarily disable
  // let TimeChart = zc.TimeChart;
  const [showNoData, setShowNoData] = useState(false);
  const parentElement = useRef(null);
  const [reservationChartData, setReservationChartData] = useState([]);

  const updateDataAvailability = () => {
    parentElement.current = document?.getElementById('restaurant-analytics');

    if (parentElement?.current?.childNodes?.length <= 0) {
      setShowNoData(true);
    } else {
      setShowNoData(false);
    }
  };

  const currentHotelName = getSelectedHotelName(selectedHotelList, selectedHotel);

  useEffect(() => {
    onFetchReservationData();
    onFetchTotalReservationData();
    updateDataAvailability();
  }, [hotelId, startDate, endDate, restaurantId]);

  const epoch = (date) => {
    return moment(date).unix();
  };

  useEffect(() => {
    try {
      if (totalReservationData && totalReservationData?.dailyTotal?.values) {
        const sortedData = totalReservationData?.dailyTotal?.values
          ?.map(({ date, orderCount }) => ({
            Date: moment(new Date(date)).format(statisticsDataKeys.dateFormat),
            orderCount,
            [statisticsDataKeys.reservationCount]: orderCount,
          }))
          ?.sort((a, b) => new Date(a.Date) - new Date(b.Date));

        setReservationChartData(sortedData);
      }
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.error(error.message);
      }
      if (error.message === 'Invalid category list') {
        notificationManager('danger', error.message);
      }
      return;
    }
  }, [totalReservationData, restaurantId]);

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onFetchReservationData = () => {
    const payload = {
      hotelId,
      startDate: startDate,
      endDate: endDate,
      restaurantId,
    };
    dispatch(fetchRestaurantReservationList(payload));
  };

  const onFetchTotalReservationData = (option) => {
    const payload = {
      hotelId,
      startDate: startDate,
      endDate: endDate,
      restaurantId,
      frequency: '',
    };
    dispatch(fetchTotalReservationsStats(payload));
  };

  const addWaterMark = (doc) => {
    var totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(150);
      doc.setTextColor('#E5E5E5');
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.5 }));
      doc.text('Hudini', 50, doc.internal.pageSize.height - 50, {
        angle: 45,
      });
      doc.restoreGraphicsState();
    }

    return doc;
  };

  const addPageNumber = (doc) => {
    var totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(12);
      doc.setTextColor('#00000');
      doc.text(100, doc.internal.pageSize.height - 5, `${i} of ${totalPages}`);
    }

    return doc;
  };

  const generateReservationListPdf = () => {
    const tempData = reservationTableData?.map((x) => {
      return {
        ...x,
        newDate: moment.unix(x.date).format(statisticsDataKeys?.errorReportDateFormat),
      };
    });

    let body = generatePdfTable(tempData, ['newDate', 'guestName', 'bookingNumber', 'noOfGuests']);
    const header = ['Date', 'Guest Name', 'Booking Number', 'No Of Guests'];
    let doc = new jsPDF();

    doc.setTextColor('#6B6B6B');
    doc.setFontSize(32);

    // the "Top Heading"
    const titleTextList = 'RESTAURANT';
    const titleWidth = (doc.getStringUnitWidth(titleTextList) * 32) / doc.internal.scaleFactor;
    const centerX = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(centerX, 20, titleTextList).setFont(undefined, 'bold');
    doc.setTextColor('#00000');
    doc.setFontSize(24);

    // the "Sub Heading" subheading
    const subTitleTextList = 'Reservation List';
    const subTitleWidth =
      (doc.getStringUnitWidth(subTitleTextList) * 24) / doc.internal.scaleFactor;
    const subTitleCenterX = (doc.internal.pageSize.width - subTitleWidth) / 2;
    doc.text(subTitleCenterX, 35, subTitleTextList).setFont(undefined, 'bold');
    doc.setTextColor('#00000');
    doc.setFontSize(12);
    doc.text(20, 50, 'Generated Date:').setFont(undefined, 'bold');
    doc
      .text(70, 50, moment().format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');
    doc.text(20, 55, 'From Date:').setFont(undefined, 'bold');
    doc
      .text(70, 55, moment(startDate).format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');
    doc.text(20, 60, 'To Date:').setFont(undefined, 'bold');
    doc
      .text(70, 60, moment(endDate).format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');

    autoTable(doc, {
      headStyles: { fillColor: '#828282' },
      head: [header],
      body: body,
      startY: 70,
      theme: 'striped',
    });
    doc = addWaterMark(doc);
    doc = addPageNumber(doc);
    doc.save('Elevate-Reservation-list.pdf');
  };

  const generateTotalReservationPdf = () => {
    let body = [];
    let header = [];
    if (totalReservationData ?? totalReservationData?.dailyTotal?.values) {
      let tempData = JSON.parse(totalReservationData?.dailyTotal?.values);
      body = generateChartPdfExportData(tempData);
      const tempRest = totalReservationData?.dailyTotal?.restaurants;
      header = ['Date', ...tempRest];
    }
    let doc = new jsPDF(
      header.length > 10 ? { orientation: 'landscape' } : { orientation: 'portrait' },
    );
    doc.setTextColor('#6B6B6B');
    doc.setFontSize(32);

    // the "Top Heading"
    const titleText = 'RESTAURANT';
    const titleWidth = (doc.getStringUnitWidth(titleText) * 32) / doc.internal.scaleFactor;
    const centerX = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(centerX, 20, titleText).setFont(undefined, 'bold');
    doc.setTextColor('#00000');
    doc.setFontSize(24);

    // the "Sub Heading" subheading
    const subTitleText = 'Reservations';
    const subTitleWidth = (doc.getStringUnitWidth(subTitleText) * 24) / doc.internal.scaleFactor;
    const subTitleCenterX = (doc.internal.pageSize.width - subTitleWidth) / 2;
    doc.text(subTitleCenterX, 35, subTitleText).setFont(undefined, 'bold');

    doc.setTextColor('#00000');
    doc.setFontSize(12);
    doc.text(20, 50, 'Generated Date:').setFont(undefined, 'bold');
    doc
      .text(70, 50, moment().format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');
    doc.text(20, 55, 'From Date:').setFont(undefined, 'bold');
    doc
      .text(70, 55, moment(startDate).format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');
    doc.text(20, 60, 'To Date:').setFont(undefined, 'bold');
    doc
      .text(70, 60, moment(endDate).format(statisticsDataKeys?.errorReportDateFormat))
      .setFont(undefined, 'bold');
    doc.text(20, 70, 'Total Reservations:').setFont(undefined, 'normal');
    doc.text(70, 70, totalReservationData?.total.toString()).setFont(undefined, 'normal');
    if (header.length > 20) {
      let lengthY = 80,
        start = 0,
        end = 15,
        loopStart = true;
      while (loopStart) {
        if (header.length > end) {
          doc.autoTable({
            headStyles: {
              fillColor: '#828282',
            },
            head: [
              start !== 0 ? ['Date', ...header.slice(start, end)] : [...header.slice(start, end)],
            ],
            body:
              start !== 0
                ? body.map((item) => [item[0], ...item.slice(start, end)])
                : body.map((item) => [...item.slice(start, end)]),
            startY: lengthY,
            theme: 'striped',
          });
          start = end;
          end += 15;
        } else {
          loopStart = false;
        }
        lengthY += body.length * 20;
      }
    } else {
      doc.autoTable({
        headStyles: { fillColor: '#828282' },
        head: [header],
        body: body,
        startY: 80,
        theme: 'striped',
      });
    }
    doc = addWaterMark(doc);
    doc = addPageNumber(doc);
    doc.save('Elevate-Total-Reservation.pdf');
  };

  const reservationSummaryMatrix = [
    {
      title: 'Total Reservations',
      subTitle: 'Total Count',
      totalCount: totalReservationData?.total || 0,
      uniqueCount: '',
    },
    {
      title: 'Total Guests',
      subTitle: 'Total Count',
      totalCount: totalReservationData?.totalGuestCount || 0,
      uniqueCount: '',
    },
  ];

  return (
    <>
      {showNoData ? (
        <span className='py-2'>
          Unable to generate reports due to third-party integration limitations.
        </span>
      ) : null}
      <div id={'Reservations'} className='d-flex flex-column gap-3'>
        <CardWrapper
          title='Reservations'
          hotelName={currentHotelName}
          startDate={startDate}
          endDate={endDate}
          showExportButton={true}
          onClickRefreshBtn={onFetchTotalReservationData}
          exportData={generateTotalReservationPdf}
          excelData={generateTotalReservationExportData(
            totalReservationData,
            startDate,
            endDate,
            'reservation',
          )}
          showLoader={showLoader}
          showAllForReport={showAllForReport}
          setShowAllForReport={setShowAllForReport}
        >
          <div className='d-flex flex-wrap p-3 col-12'>
            {reservationSummaryMatrix?.map((summary, index) => (
              <SummaryCardWrapper
                key={index}
                title={summary?.title}
                subTitle={summary?.subTitle}
                total={summary?.totalCount?.toLocaleString()}
                uniqueCount={summary?.uniqueCount}
              />
            ))}
          </div>
          {reservationChartData?.length > 0 && (
            <WidgetWrapper
              title={'Total Reservations'}
              width='95%'
              isCustom={true}
              className='px-3'
            >
              <CustomLineChart
                xAxis={statisticsDataKeys?.date}
                yAxis={statisticsDataKeys?.orderCount}
                data={reservationChartData?.map((order, index) => {
                  return {
                    [statisticsDataKeys.date]: order?.Date,
                    [statisticsDataKeys.orderCount]: order?.orderCount,
                    [statisticsDataKeys.reservationCount]:
                      order?.[statisticsDataKeys?.reservationCount],
                  };
                })}
                sliderX={true}
                color={'#ff9800'}
                tooltipItems={[statisticsDataKeys?.reservationCount]}
              />
            </WidgetWrapper>
          )}
        </CardWrapper>

        {reservationTableData?.length > 0 && (
          <CardWrapper
            title='Reservation List'
            start={startDate}
            end={endDate}
            onClickRefreshBtn={onFetchReservationData}
            exportData={generateReservationListPdf}
            excelData={generateReservationExportData(
              reservationTableData,
              startDate,
              endDate,
              'reservation',
            )}
          >
            <div className='row mx-2 d-flex mt-4'>
              <TableContainer>
                <Table className='list-table ' aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Guest Name</TableCell>
                      <TableCell>Booking Number</TableCell>
                      <TableCell>No of Guest</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? reservationTableData
                          ?.slice()
                          ?.sort((a, b) => b?.date - a?.date)
                          ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : reservationTableData
                    )?.map((user, i) => (
                      <TableRow key={i} className='border-white pointer'>
                        <TableCell>{moment.unix(user?.date).format('DD MMM, YYYY')}</TableCell>
                        <TableCell>{user?.guestName}</TableCell>
                        <TableCell>{user?.bookingNumber || '---'}</TableCell>
                        <TableCell>{user?.noOfGuests}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {!showAllForReport && (
                    <TableFooter>
                      <TableRow className='paginator'>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                          count={reservationTableData?.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          SelectProps={{
                            inputProps: { 'aria-label': 'Items per page' },
                            native: true,
                          }}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </div>
          </CardWrapper>
        )}

        <Restaurant
          key='Restaurant'
          allStatisticsData={totalReservationData?.fnBStats}
          selectedRestaurant={{ value: [restaurantId] }}
          showAllForReport={showAllForReport}
        />
      </div>
    </>
  );
};

export default Reservations;
